import { request } from './_request.js';
import type { ChildTicketRow } from '@cortex/domain-product-innovation-lab-basic-design-tree';

/** 展開の入力。 */
export interface ExpandBasicDesignInput {
  /** シートの行（この順で子票を作る）。 */
  rows: ChildTicketRow[];
  /** 基本設計開始予定日（YYYY-MM-DD）。 */
  startDate: string;
  /** 基本設計終了予定日（YYYY-MM-DD）。 */
  endDate: string;
  /** 開発リーダーを押した人へ付け替えるか。 */
  takeOverDevLeader: boolean;
  /** 基本設計シートの URL（基本設計の子票 description に残る）。 */
  sheetUrl: string;
}

/** 展開で作った / 残した子票 1 枚。 */
export interface ExpandedChild {
  /** シートの行 id。 */
  rowId: string;
  /** 種別 id。 */
  kindId: string;
  /** Backlog の課題キー。 */
  issueKey: string;
  /** Backlog の課題 id。 */
  issueId: number;
}

/** 展開の結果。 */
export interface ExpandBasicDesignResponse {
  /** 作った / 残した子票。 */
  children: ExpandedChild[];
  /** 消した課題キー。 */
  removed: string[];
  /** 完了済み / 実績pt ありで残した課題キー。 */
  retained: string[];
  /** 母票へ書いた合計 pt。 */
  totalPoint: number;
}

/** 展開履歴 1 件。 */
export interface BasicDesignExpansionHistoryItem {
  /** Firestore の doc id。 */
  id?: string;
  /** 母票の課題キー。 */
  issueKey: string;
  /** 展開した時刻（ISO8601）。 */
  expandedAt: string;
  /** 展開した人。 */
  expandedBy: string;
  /** 合計 pt。 */
  totalPoint: number;
  /** 基本設計の pt。 */
  basicDesignPoint: number;
  /** 基本設計開始予定日。 */
  startDate: string;
  /** 基本設計終了予定日。 */
  endDate: string;
  /**
   * この回に展開したシートの行。履歴の表示と再編集の起点に使う。
   * 行を残すようになる前の記録は空配列で返る。
   */
  rows: ChildTicketRow[];
  /** この回に存在する子票。 */
  children: ExpandedChild[];
  /** この回に消した課題キー。 */
  removed: string[];
  /** 消さずに残した課題キー。 */
  retained: string[];
  /** Backlog への書き込みの途中で失敗した回なら true。 */
  incomplete?: boolean;
}

/** チャンク 1 つぶんの展開入力。 */
export interface ExpandChunkInput {
  /** 画面が持つチャンクの一意キー。 */
  chunkId: string;
  /** 並び（= 統合プロダクト内リリース順、1 始まり）。 */
  order: number;
  /** チャンク名。 */
  title: string;
  /** そのチャンクのシート行。 */
  rows: ChildTicketRow[];
}

/** チャンク分け展開の入力。 */
export interface ExpandChunksInput {
  /** 展開するチャンク（画面の並び順）。 */
  chunks: ExpandChunkInput[];
  /** 基本設計開始予定日（YYYY-MM-DD）。 */
  startDate: string;
  /** 基本設計終了予定日（YYYY-MM-DD）。 */
  endDate: string;
  /** 開発リーダーを押した人へ付け替えるか。 */
  takeOverDevLeader: boolean;
  /** 基本設計シートの URL。 */
  sheetUrl: string;
}

/** チャンク 1 つぶんの展開結果。 */
export interface ExpandedChunk {
  /** 画面が持つチャンクの一意キー。 */
  chunkId: string;
  /** 母票（プロダクト一覧）の課題キー。 */
  motohyoKey: string;
  /** 中継チケットの課題キー。 */
  relayKey: string;
  /** そのチャンクの子票。 */
  children: ExpandedChild[];
  /** 消した課題キー。 */
  removed: string[];
  /** 完了済み / 実績pt ありで残した課題キー。 */
  retained: string[];
  /** 母票へ書いた合計 pt。 */
  totalPoint: number;
}

/** チャンク分け展開の結果。 */
export interface ExpandChunksResponse {
  /** 爺票（統合プロダクト）の課題キー。 */
  gichyoKey: string;
  /** チャンクごとの結果。 */
  chunks: ExpandedChunk[];
}

/** 展開済みチャンクの台帳 1 件。 */
export interface BasicDesignChunkItem {
  /** 画面が持つチャンクの一意キー。 */
  chunkId: string;
  /** 並び（= リリース順）。 */
  order: number;
  /** チャンク名。 */
  title: string;
  /** 母票の課題キー。 */
  motohyoKey: string;
  /** 母票の課題 id。 */
  motohyoId: number;
  /** 中継チケットの課題キー（母票を作った直後に落ちた回は null）。 */
  relayKey: string | null;
  /** 最後に展開した時刻（ISO8601）。 */
  expandedAt: string;
}

/**
 * 基本設計展開の API クライアント。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計シートを Backlog の子票へ展開し、展開履歴を読む API クライアント
 * @graph-connects /basic-design/:pjKey/expand [calls, via:POST] シートを Backlog の子票へ展開する
 * @graph-connects /basic-design/:pjKey/expansion-history [calls, via:GET] 展開履歴を新しい順に読む
 * @graph-connects /basic-design/:pjKey/expand-chunks [calls, via:POST] チャンクに分けて Backlog へ展開する
 * @graph-connects /basic-design/:pjKey/chunks [calls, via:GET] 展開済みチャンクの台帳を読む
 */
export const basicDesignExpansionApi = {
  /** シートを Backlog の子票へ展開する。 */
  expand: async (
    pjKey: string,
    payload: ExpandBasicDesignInput,
  ): Promise<ExpandBasicDesignResponse> =>
    request<ExpandBasicDesignResponse>(`/basic-design/${encodeURIComponent(pjKey)}/expand`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /**
   * 展開履歴を新しい順に読む。
   *
   * `rows` が無い記録（行を残すようになる前の展開、または API が先に古い版で動いている間）を
   * 空配列へ揃えてから返す。画面側は行を必ずあるものとして読むので、ここで形を合わせる。
   */
  listHistory: async (pjKey: string): Promise<BasicDesignExpansionHistoryItem[]> => {
    const response = await request<{ items: BasicDesignExpansionHistoryItem[] }>(
      `/basic-design/${encodeURIComponent(pjKey)}/expansion-history`,
    );
    return response.items.map((item) => ({ ...item, rows: item.rows ?? [] }));
  },

  /** チャンクに分けて Backlog へ展開する。 */
  expandChunks: async (pjKey: string, payload: ExpandChunksInput): Promise<ExpandChunksResponse> =>
    request<ExpandChunksResponse>(`/basic-design/${encodeURIComponent(pjKey)}/expand-chunks`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /** 展開済みチャンクの台帳を並び順で読む。 */
  listChunks: async (pjKey: string): Promise<BasicDesignChunkItem[]> => {
    const response = await request<{ items: BasicDesignChunkItem[] }>(
      `/basic-design/${encodeURIComponent(pjKey)}/chunks`,
    );
    return response.items;
  },
};
